<script setup lang="ts">
import { computed } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import DynamicForm from './DynamicForm.vue'
import type {
  FormBlock,
  WebsiteFormCaptureState,
  WebsiteFormSubmission,
} from '../../types'

const props = defineProps<{
  block: FormBlock
  formCapture?: WebsiteFormCaptureState
}>()
const emit = defineEmits<{ formSubmit: [submission: WebsiteFormSubmission] }>()
const effectiveCapture = computed<WebsiteFormCaptureState>(() => props.formCapture ?? ({
  enabled: true,
  submitting: false,
  succeeded: false,
}))
</script>

<template>
  <section :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <template v-if="block.variant === 'split'">
      <div class="site-site-container site:grid site:items-start site:gap-10 site:@min-[64rem]:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] site:@min-[64rem]:gap-16">
        <div class="site:pt-3">
          <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
          <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
          <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        </div>
        <div class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-8" :style="{ borderRadius: 'var(--site-radius-lg)' }">
          <DynamicForm :form-id="block.content.formId ?? ''" :form-capture="effectiveCapture" :submit-label="block.content.submitLabel" @submit="emit('formSubmit', $event)" />
        </div>
      </div>
    </template>

    <template v-else-if="block.variant === 'centered'">
      <div class="site-site-container">
        <div class="site:mx-auto site:max-w-3xl site:text-center">
          <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
          <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
          <SiteEditableText v-model="block.content.description" as="p" class="site:mx-auto site:mt-5 site:max-w-2xl site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        </div>
        <div class="site:mx-auto site:mt-10 site:max-w-2xl site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-10" :style="{ borderRadius: 'var(--site-radius-lg)' }">
          <DynamicForm :form-id="block.content.formId ?? ''" :form-capture="effectiveCapture" :submit-label="block.content.submitLabel" @submit="emit('formSubmit', $event)" />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="site-site-container">
        <div class="site:mb-8 site:max-w-2xl">
          <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
          <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-2 site:text-2xl site:font-bold site:tracking-tight site:@min-[40rem]:text-4xl" />
          <SiteEditableText v-if="block.content.description" v-model="block.content.description" as="p" class="site:mt-3 site:text-base site:leading-7 site:text-(--site-color-muted-text)" />
        </div>
        <div class="site:max-w-3xl">
          <DynamicForm :form-id="block.content.formId ?? ''" :form-capture="effectiveCapture" :submit-label="block.content.submitLabel" @submit="emit('formSubmit', $event)" />
        </div>
      </div>
    </template>
  </section>
</template>
